/**
 * Booking Query Hooks
 * TanStack Query hooks for booking operations
 * Replaces Redux actions, epics, and reducers
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  fetchBooking,
  checkBookingSession,
  acceptBooking,
  declineBooking,
  acceptBookingWithVerification,
} from '../services/api';
import type { Booking } from '../types';

// Mutation parameters
interface BookingMutationParams {
  bookingId: number;
  uuid?: string;
}

// Query keys for cache management
export const bookingKeys = {
  all: ['booking'] as const,
  detail: (uuid: string) => ['booking', uuid] as const,
  session: (uuid: string) => ['booking', 'session', uuid] as const,
};

/**
 * Hook to load booking data
 * Replaces: LOAD_BOOKING action + fetchBookingsEpic
 */
export function useBookingQuery(
  uuid: string | undefined,
  options: Omit<UseQueryOptions<Booking, Error>, 'queryKey' | 'queryFn'> = {}
) {
  return useQuery({
    queryKey: bookingKeys.detail(uuid || ''),
    queryFn: () => fetchBooking(uuid!),
    enabled: !!uuid,
    staleTime: 30000, // Consider data fresh for 30 seconds
    retry: 1, // Only retry once on failure
    ...options,
  });
}

/**
 * Hook to check booking session status
 * Replaces: CHECK_BOOKING action + checkBookingEpic
 */
export function useBookingSessionCheck(
  uuid: string | undefined,
  options: Omit<UseQueryOptions<Booking, Error>, 'queryKey' | 'queryFn'> = {}
) {
  return useQuery({
    queryKey: bookingKeys.session(uuid || ''),
    queryFn: () => checkBookingSession(uuid!),
    enabled: false, // Only run when explicitly called via refetch
    ...options,
  });
}

/**
 * Hook to accept a booking (on-account)
 * Replaces: ACCEPT_BOOKING action + acceptBookingsEpic
 */
export function useAcceptBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId }: BookingMutationParams) => acceptBooking(bookingId),
    onSuccess: (_data: void, { uuid }: BookingMutationParams) => {
      // Invalidate and refetch booking data after successful accept
      if (uuid) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.detail(uuid) });
      }
    },
  });
}

/**
 * Hook to decline a booking
 * Replaces: DECLINE_BOOKING action + declineBookingEpic
 */
export function useDeclineBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId }: BookingMutationParams) => declineBooking(bookingId),
    onSuccess: (_data: void, { uuid }: BookingMutationParams) => {
      // Invalidate and refetch booking data after successful decline
      if (uuid) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.detail(uuid) });
      }
    },
  });
}

/**
 * Hook to accept booking with payment verification
 * Replaces: ACCEPT_BOOKING_WITH_PAYMENT_VERIFICATION action + acceptBookingWithVerifyPayment epic
 */
export function useAcceptBookingWithVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId }: BookingMutationParams) => acceptBookingWithVerification(bookingId),
    onSuccess: (_data: void, { uuid }: BookingMutationParams) => {
      // Invalidate and refetch booking data after successful verification
      if (uuid) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.detail(uuid) });
      }
    },
  });
}

/**
 * Helper hook to refresh booking data
 * Can be used after successful operations
 */
export function useRefreshBooking() {
  const queryClient = useQueryClient();

  return (uuid: string) => {
    queryClient.invalidateQueries({ queryKey: bookingKeys.detail(uuid) });
  };
}

export default {
  useBookingQuery,
  useBookingSessionCheck,
  useAcceptBooking,
  useDeclineBooking,
  useAcceptBookingWithVerification,
  useRefreshBooking,
  bookingKeys,
};
